// src/receptionist/components/FollowUpEditor.tsx
//
// Scheduled messages around the appointment (Conversation → Follow-ups).
//
// Rewritten to read plainly (#4796 follow-up, 29 Oct 2026). Each follow-up is
// one card: a switch, its name, its template status and one sentence saying
// when it goes ("Sends 1 day before the appointment, unless they already
// replied"). Opened, it shows three labelled choices, the message with the
// placeholders as buttons that insert them, and on the right the message as the
// customer sees it with example values filled in, plus the example Meta gets
// for each placeholder.
//
// Those examples are the backend's own (whatsapp_onboarding.NAMED_SAMPLES,
// ourteam-backend#6679), in the same order it numbers them ({{1}}, {{2}} ...),
// so what the merchant reads here is what Meta is shown at submission.
//
// Each follow-up's wording IS its WhatsApp template: anything sent outside the
// 24-hour window needs Meta's approval, so the status stays on every card.

import { useRef, useState, type ReactNode } from "react";
import { AlertTriangleIcon, ChevronRightIcon, TrashIcon } from "lucide-react";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { Switch } from "@component/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@component/select";
import { FOCUS_RING, SURFACE, TYPE } from "@oura/ui";
import { cn } from "@oura/lib/cn";
import { AddButton, TagPill, WaPreview } from "./ui";
import { InfoNotice } from "./kit";
import {
    FOLLOW_UP_TRIGGER_LABELS,
    STOP_RULE_LABELS,
    describeOffset,
    needsTemplate,
    newFollowUpId,
    type FollowUp,
    type FollowUpTrigger,
    type LiveTemplateStatus,
    type StopRule,
} from "../flow";

const TEMPLATE_TAG: Record<LiveTemplateStatus, { label: string; tone: "green" | "amber" | "gray" | "red" }> = {
    approved: { label: "Template approved", tone: "green" },
    pending: { label: "Awaiting Meta", tone: "amber" },
    not_submitted: { label: "Not submitted", tone: "gray" },
    rejected: { label: "Rejected by Meta", tone: "red" },
};

/** Offsets offered per trigger, so a merchant cannot express an absurd delay. */
const OFFSETS: Record<FollowUpTrigger, { value: number; label: string }[]> = {
    booking_confirmed: [{ value: 0, label: "Straight away" }, { value: 1, label: "1 hour later" }],
    before_appointment: [
        { value: -48, label: "2 days before" }, { value: -24, label: "1 day before" },
        { value: -3, label: "3 hours before" }, { value: -2, label: "2 hours before" },
    ],
    after_appointment: [
        { value: 24, label: "1 day after" }, { value: 72, label: "3 days after" },
        { value: 336, label: "2 weeks after" }, { value: 672, label: "4 weeks after" },
    ],
    flow_stalled: [
        { value: 1, label: "After 1 hour" }, { value: 3, label: "After 3 hours" },
        { value: 24, label: "After 1 day" },
    ],
    no_show: [{ value: 2, label: "2 hours after" }, { value: 24, label: "1 day after" }],
};

/** What each placeholder becomes, and the example Meta is shown for it (backend NAMED_SAMPLES). */
const PLACEHOLDERS: { name: string; means: string; example: string }[] = [
    { name: "service", means: "the treatment booked", example: "Hydra Glow Facial" },
    { name: "date", means: "the appointment day", example: "Fri 31 Oct" },
    { name: "time", means: "the appointment time", example: "3:00 PM" },
    { name: "name", means: "the customer's name", example: "Aina" },
    { name: "merchant", means: "your business name", example: "The Lush Clinic" },
    { name: "directions", means: "your FAQ note titled Parking", example: "We are on level 2, next to the lift." },
];
const EXAMPLE: Record<string, string> = Object.fromEntries(PLACEHOLDERS.map((p) => [p.name, p.example]));

/** The placeholders a message uses, in order of first appearance: the order the backend numbers them. */
function usedPlaceholders(message: string): string[] {
    const seen: string[] = [];
    const re = /\{(\w+)\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(message)) !== null) if (!seen.includes(m[1])) seen.push(m[1]);
    return seen;
}

function withExamples(message: string): string {
    return message.replace(/\{(\w+)\}/g, (all, name: string) => EXAMPLE[name] ?? all);
}

/** One sentence for when it goes, in the merchant's words. */
function whenSentence(f: FollowUp): string {
    const offset = describeOffset(f).toLowerCase();
    const base =
        f.trigger === "booking_confirmed" ? (f.offsetHours === 0 ? "Sends as soon as your team confirms a booking" : `Sends ${offset} your team confirms a booking`)
        : f.trigger === "before_appointment" ? `Sends ${offset} the appointment`
        : f.trigger === "after_appointment" ? `Sends ${offset} the appointment`
        : f.trigger === "flow_stalled" ? `Sends when a customer stops replying mid-booking (${offset})`
        : `Sends ${offset} a missed appointment`;
    const stop = f.stopIf === "customer_replied" ? ", unless they have already replied" : f.stopIf === "rebooked" ? ", unless they have already rebooked" : "";
    return `${base}${stop}.`;
}

function blankFollowUp(): FollowUp {
    return {
        id: newFollowUpId(), enabled: false, label: "New follow-up",
        trigger: "after_appointment", offsetHours: 72, message: "",
        templateStatus: "not_submitted", stopIf: "none",
    };
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={htmlFor} className={cn(TYPE.lab, "block")}>{label}</Label>
            {children}
        </div>
    );
}

function FollowUpCard({
    f, status, disabled, startOpen, onPatch, onRemove,
}: {
    f: FollowUp;
    /** The template's real state (GET .../whatsapp), or the flow's own copy. */
    status: LiveTemplateStatus;
    disabled?: boolean;
    startOpen: boolean;
    onPatch: (next: Partial<FollowUp>) => void;
    onRemove: () => void;
}) {
    const [open, setOpen] = useState(startOpen);
    const box = useRef<HTMLTextAreaElement>(null);
    const warn = f.enabled && needsTemplate(f) && status !== "approved";
    const tag = TEMPLATE_TAG[status];
    const used = usedPlaceholders(f.message);

    /** Put `{name}` where the cursor is, or at the end. */
    const insert = (name: string) => {
        const el = box.current;
        const token = `{${name}}`;
        const at = el?.selectionStart ?? f.message.length;
        const end = el?.selectionEnd ?? at;
        onPatch({ message: f.message.slice(0, at) + token + f.message.slice(end) });
        requestAnimationFrame(() => {
            el?.focus();
            el?.setSelectionRange(at + token.length, at + token.length);
        });
    };

    return (
        <section className={cn(SURFACE.card, "p-5", warn && "ring-amber-300")}>
            <div className="flex items-start gap-3">
                <Switch
                    checked={f.enabled}
                    disabled={disabled}
                    onCheckedChange={(v) => onPatch({ enabled: v })}
                    aria-label={`${f.label}: ${f.enabled ? "on" : "off"}`}
                    className="mt-0.5 flex-shrink-0"
                />
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    className={cn("min-w-0 flex-1 rounded-md text-left", FOCUS_RING)}
                >
                    <span className={cn(TYPE.tb, "flex items-center gap-1.5 font-semibold", f.enabled ? "text-gray-900" : "text-gray-500")}>
                        {f.label || "Untitled follow-up"}
                        <ChevronRightIcon aria-hidden size={14} className={cn("shrink-0 text-gray-400 transition-transform", open && "rotate-90")} />
                    </span>
                    <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>
                        {f.enabled ? whenSentence(f) : "Off: nothing is sent."}
                    </span>
                    {!open && f.message.trim() && (
                        <span className={cn(TYPE.tc, "mt-1 block truncate text-gray-700")}>“{withExamples(f.message)}”</span>
                    )}
                </button>
                {needsTemplate(f) && <TagPill tone={tag.tone}>{tag.label}</TagPill>}
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onRemove}
                    className={cn("rounded-full p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600", FOCUS_RING)}
                    aria-label={`Remove ${f.label}`}
                    title="Remove this follow-up"
                >
                    <TrashIcon className="h-4 w-4" />
                </button>
            </div>

            {/* The page's notice lists every follow-up that cannot send; opened, the card says why. */}
            {warn && open && (
                <p className={cn(TYPE.tc, "m-0 mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-amber-800 ring-1 ring-inset ring-amber-200")}>
                    <AlertTriangleIcon aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
                    {status === "rejected"
                        ? "Meta rejected its template, so it reaches no one. Resubmit it from Setup → Message templates."
                        : "On, but its template is not approved yet, so it reaches no one. Submit it from Setup → Message templates."}
                </p>
            )}

            {open && (
                <div className="mt-4 grid gap-5 border-t border-gray-100 pt-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="min-w-0 space-y-4">
                        <Field label="Name" htmlFor={`fu-name-${f.id}`}>
                            <Input id={`fu-name-${f.id}`} value={f.label} disabled={disabled} onChange={(e) => onPatch({ label: e.target.value })} />
                        </Field>
                        <div className="grid gap-3 sm:grid-cols-3">
                            <Field label="Send it">
                                <Select
                                    value={f.trigger}
                                    disabled={disabled}
                                    onValueChange={(v) => {
                                        const trigger = v as FollowUpTrigger;
                                        onPatch({ trigger, offsetHours: OFFSETS[trigger][0].value });
                                    }}
                                >
                                    <SelectTrigger className="w-full border border-gray-300"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(Object.keys(FOLLOW_UP_TRIGGER_LABELS) as FollowUpTrigger[]).map((tr) => (
                                            <SelectItem key={tr} value={tr}>{FOLLOW_UP_TRIGGER_LABELS[tr]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Timing">
                                <Select value={String(f.offsetHours)} disabled={disabled} onValueChange={(v) => onPatch({ offsetHours: Number(v) })}>
                                    <SelectTrigger className="w-full border border-gray-300"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {OFFSETS[f.trigger].map((o) => (
                                            <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Unless">
                                <Select value={f.stopIf} disabled={disabled} onValueChange={(v) => onPatch({ stopIf: v as StopRule })}>
                                    <SelectTrigger className="w-full border border-gray-300"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(Object.keys(STOP_RULE_LABELS) as StopRule[]).map((r) => (
                                            <SelectItem key={r} value={r}>{STOP_RULE_LABELS[r]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <Field label="Message" htmlFor={`fu-msg-${f.id}`}>
                            <Textarea
                                ref={box}
                                id={`fu-msg-${f.id}`}
                                value={f.message}
                                disabled={disabled}
                                placeholder="What the customer receives"
                                onChange={(e) => onPatch({ message: e.target.value })}
                                className="min-h-[96px] resize-y"
                            />
                        </Field>
                        <div>
                            <p className={cn(TYPE.tc, "m-0 mb-1.5 text-gray-500")}>Insert a detail, filled in when it sends:</p>
                            <div className="flex flex-wrap gap-1.5">
                                {PLACEHOLDERS.map((p) => (
                                    <button
                                        key={p.name}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => insert(p.name)}
                                        title={`{${p.name}}: ${p.means}`}
                                        className={cn("inline-flex h-7 items-center rounded-full bg-white px-3 font-mono text-xs text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 disabled:opacity-40", FOCUS_RING)}
                                    >
                                        {`{${p.name}}`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>What the customer sees</p>
                            <WaPreview text={withExamples(f.message)} note={f.enabled ? whenSentence(f) : "Off"} placeholder="Nothing written yet" />
                        </div>
                        {needsTemplate(f) && (
                            <div>
                                <p className={cn(TYPE.lab, "m-0 mb-1.5")}>Example Meta is shown</p>
                                {used.length === 0 ? (
                                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>No details to fill in, so Meta needs no example.</p>
                                ) : (
                                    <dl className="m-0 divide-y divide-gray-100 rounded-xl bg-gray-50 px-3">
                                        {used.map((name, i) => (
                                            <div key={name} className="flex items-baseline gap-2 py-2">
                                                <dt className={cn(TYPE.tc, "shrink-0 font-mono text-gray-500")}>{`{{${i + 1}}}`}</dt>
                                                <dd className={cn(TYPE.tc, "m-0 min-w-0 flex-1 text-gray-900")}>
                                                    {EXAMPLE[name] ?? `sample ${i + 1}`}
                                                    <span className="block text-gray-500">{`{${name}}`}{PLACEHOLDERS.find((p) => p.name === name) ? `, ${PLACEHOLDERS.find((p) => p.name === name)!.means}` : ""}</span>
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

export function FollowUpEditor({
    followUps, onChange, disabled, statusOf,
}: { followUps: FollowUp[]; onChange: (f: FollowUp[]) => void; disabled?: boolean; statusOf?: (f: FollowUp) => LiveTemplateStatus }) {
    const [added, setAdded] = useState<string[]>([]);
    const patch = (id: string, next: Partial<FollowUp>) =>
        onChange(followUps.map((f) => (f.id === id ? { ...f, ...next } : f)));

    return (
        <div className="space-y-3">
            <InfoNotice>
                Messages the receptionist sends on a schedule. Each one's wording becomes its WhatsApp template: it goes outside the
                24-hour window, so Meta approves it first. Submit them from Setup → Message templates.
            </InfoNotice>

            {followUps.map((f) => (
                <FollowUpCard
                    key={f.id}
                    f={f}
                    status={statusOf ? statusOf(f) : f.templateStatus}
                    disabled={disabled}
                    startOpen={added.includes(f.id)}
                    onPatch={(next) => patch(f.id, next)}
                    onRemove={() => onChange(followUps.filter((x) => x.id !== f.id))}
                />
            ))}

            <AddButton
                disabled={disabled}
                onClick={() => {
                    const fu = blankFollowUp();
                    setAdded((a) => [...a, fu.id]);
                    onChange([...followUps, fu]);
                }}
            >
                Add a follow-up
            </AddButton>
        </div>
    );
}
